import React, { useEffect, useRef, useState, useMemo, useCallback, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { updateStructureTemplate, getStructureTemplate, deleteStructureTemplate, createStructureTemplate, checkTemplateStatus, resetTemplateCreation, reanalyzeStructureTemplate, shareTemplate } from '../../store/actions/TemplateActions';
import { useHistory } from 'react-router-dom';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { PiPlusLight, PiTrashLight, PiCopyLight, PiPencilSimpleLight, PiCheckLight, PiXLight, PiCaretDownLight, PiCaretRightLight, PiCircleLight, PiStackSimpleLight, PiUserSound, PiTextAlignLeftLight, PiArticleLight, PiArrowUpLight, PiArrowDownLight, PiDotsThreeVerticalLight, PiUsersThreeLight, PiArrowsClockwiseLight, PiFileTextLight } from 'react-icons/pi';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import Badge from '../shared/TailwindComponents/Badge';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Textarea from '../shared/TailwindComponents/Textarea';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import { Field, Well } from '../shared/brand/Field';
import { showModal, hideModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import { AIProgressPanel, useAIRunElapsed } from '../shared/brand/aiProgress';
import DetailFrame from '../shared/Frames/DetailFrame';
import { Menu, Transition } from '@headlessui/react';
import clsx from 'clsx';
import CollaboratorSelector, { Collaborator } from '../shared/CollaboratorSelector';
import { BaseForm, BaseFormFieldConfig, BaseFormSectionConfig, OrgWideToggle } from '../shared/FormComponents';
import { FormSchema, FormField } from '../../utils/validation/form-schema';
import { getMultiHubSocketService } from '../../utils/MultiHubSocketService';
import { TEMPLATE_CREATE_COMPLETED, TEMPLATE_CREATE_FAILED, TEMPLATE_CREATE_EVENT } from '../../store/types';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, createStructureFormTourSteps, structureDetailTourSteps } from '../shared/FeatureTour';
import TemplateTypeSwitcher from './TemplateTypeSwitcher';
import { toErrorText, ensureDisplayString } from '../../utils/errorMessage';

interface StructureFormProps {
  templateId?: string;
}

interface OutlineItem {
  level: number;
  title: string;
  content_prompt: string;
  example_language: string;
  fallbacks: string[];
  subsections: OutlineItem[];
  number?: string;
}

interface StructureFormValues {
  TemplateName: string;
  DraftType: string;
  OwnerType: string;
  SharedWith: { UserID: string; Name: string; Email: string; ProfilePicture: string | null; Initials: string; Access?: string }[];  // Typed as array of objects
  Description: string;
  Structure: { outline: OutlineItem[] };
  Tone: string;
  WritingStyle: string;
  document?: File | null;
  CreatedAt?: string;
  UpdatedAt?: string;
  Owner: { UserID: string; Name: string; Email: string; ProfilePicture: string | null; Initials: string };  // Add Owner as object
}

const subsectionVariants: Variants = {
  hidden: { opacity: 0, height: 0 },
  visible: { opacity: 1, height: 'auto', transition: { duration: 0.2 } },
  exit: { opacity: 0, height: 0, transition: { duration: 0.2 } },
};

/**
 * An outline title as the outline should SHOW it.
 *
 * Titles come out of document extraction verbatim, so they arrive wearing
 * whatever the source had: markdown hashes and bold markers, the source's own
 * numbering ("1.", "1.2", "Article 3:", "IV.") in front of the number we
 * assign, HTML entities, zero-width and control characters, runs of
 * whitespace and newlines. Display only - the stored title is untouched.
 */
const ENTITY_MAP: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'" };
export const cleanOutlineTitle = (raw: unknown): string => {
  if (typeof raw !== 'string') return '';
  let s = raw
    .replace(/&(amp|lt|gt|quot|apos|nbsp|#39);/g, (_m, e: string) => ENTITY_MAP[e] ?? '')
    // zero-width, BOM, soft hyphen and replacement characters vanish...
    .replace(/[\u00AD\u200B-\u200D\u2060\uFEFF\uFFFD]/g, '')
    // ...control characters and line/paragraph separators become spaces
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001F\u007F-\u009F\u200E\u200F\u2028-\u202F]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  // markdown heading / list / emphasis markers
  s = s.replace(/^(#{1,6}|[-*+>])\s+/, '').replace(/(\*\*|__|`)/g, '').replace(/^\*(.+)\*$/, '$1');
  // the source's own numbering, which duplicates the number we draw
  s = s
    .replace(/^(section|article|clause|chapter|part|§)(\s*\d+|\s+[IVXLC]+\b)(\.\d+)*[.:)]?\s*[-–—:]?\s*/i, '')
    .replace(/^(\d+(\.\d+)*|[IVXLC]+|[A-Za-z])[.)]\s+/, '')
    .replace(/^\d+(\.\d+)+\s+/, '')
    .replace(/^[-–—:.\s]+/, '');
  // A title that was ONLY a number ("Section 4") keeps its original words.
  return s.trim() || raw.replace(/\s+/g, ' ').trim();
};

const DraggableSection = ({ item, index, formik, setEditingSectionIndex, sectionRefs, editingSectionIndex, editingSubsectionKey, setEditingSubsectionKey }: { item: OutlineItem & { number: string }; index: number; formik: any; setEditingSectionIndex: (index: number | null) => void; sectionRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>; editingSectionIndex: any; editingSubsectionKey: string | null; setEditingSubsectionKey: (key: string | null) => void }) => {
  const { t } = useTranslation();
  /*
    Reordering is explicit: move up / move down in the header. The whole card
    used to be a react-dnd drag source AND drop target, so selecting text in a
    prompt, or scrolling past a card, shuffled the outline under the cursor.
  */
  const outline = formik.values.Structure.outline as OutlineItem[];
  const moveSection = (to: number) => {
    if (to < 0 || to >= outline.length) return;
    const next = [...outline];
    const [moved] = next.splice(index, 1);
    next.splice(to, 0, moved);
    formik.setFieldValue('Structure.outline', next);
  };

  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (ref.current) {
      sectionRefs.current[item.number] = ref.current;
    }
  }, [item.number, sectionRefs]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <div className='glass rounded-lg shadow-sm overflow-hidden'>
        <div className="flex items-center justify-between px-3 py-2 border-b border-hairline-soft">
          {editingSectionIndex === index ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                name={`Structure.outline.${index}.title`}
                value={item.title}
                onChange={formik.handleChange}
                className="flex-1 text-sm"
                type="text"
              />
              <button onClick={() => setEditingSectionIndex(null)} className="text-success-500 hover:text-success-600 p-1"><PiCheckLight size={20} /></button>
              <button onClick={() => setEditingSectionIndex(null)} className="text-danger-500 hover:text-danger-600 p-1"><PiXLight size={20} /></button>
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-1 min-w-0 group cursor-pointer" onClick={() => setEditingSectionIndex(index)}>
              <span className="text-xs font-mono text-fg-muted bg-surface-sunken border border-hairline-soft px-1.5 py-0.5 rounded flex-shrink-0">{item.number}</span>
              <h3 className="text-sm font-medium text-fg-strong transition-colors break-words min-w-0">{cleanOutlineTitle(item.title) || t('Untitled section')}</h3>
              <PiPencilSimpleLight className="text-fg-muted opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" size={16} />
            </div>
          )}
          <div className="flex items-center gap-1 flex-shrink-0 ms-2">
            <Button
              variant="outline"
              size="small"
              onClick={() => moveSection(index - 1)}
              disabled={index === 0}
              ariaLabel={t('Move up')}
              tooltip={t('Move up')}
              className="!p-1.5"
            >
              <PiArrowUpLight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="small"
              onClick={() => moveSection(index + 1)}
              disabled={index === outline.length - 1}
              ariaLabel={t('Move down')}
              tooltip={t('Move down')}
              className="!p-1.5"
            >
              <PiArrowDownLight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {/* Grey body, white fields: `surface-canvas` remaps the field token
            to white inside it (index.css). */}
        <div className='surface-canvas bg-surface-sunken/60 p-4 space-y-4'>
          <Textarea
            id={`outline-${index}-content-prompt`}
            name={`Structure.outline.${index}.content_prompt`}
            label={t('Content prompt')}
            value={item.content_prompt}
            onChange={formik.handleChange}
            placeholder={t('Describe what content should go in this section...')}
            rows={4}
          />
          <Textarea
            id={`outline-${index}-example-language`}
            name={`Structure.outline.${index}.example_language`}
            label={t('Example language')}
            value={item.example_language}
            onChange={formik.handleChange}
            placeholder={t('Add example phrasing or language samples...')}
            rows={4}
          />
          <div>
            {/* Names the fallback list, not a single control - a heading, not a <label>. */}
            <h5 className="text-sm font-medium text-fg-body flex items-center gap-2 mb-2">
              <PiCircleLight size={16} className="text-fg-muted" /> {t('Fallbacks')}
            </h5>
            <FieldArray name={`Structure.outline.${index}.fallbacks`}>
              {({ remove: removeFallback, push: pushFallback }) => (
                <div className="space-y-3">
                  <AnimatePresence>
                    {item.fallbacks.map((fallback, fbIndex) => (
                      <motion.div
                        key={fbIndex}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="flex items-center gap-2"
                      >
                        <span className="text-xs text-fg-muted w-6">{fbIndex + 1}.</span>
                        <Input
                          id={`outline-${index}-fallback-${fbIndex}`}
                          name={`Structure.outline.${index}.fallbacks.${fbIndex}`}
                          value={fallback}
                          onChange={formik.handleChange}
                          placeholder={t('Alternative phrasing...')}
                          type="text"
                        />
                        <button onClick={() => removeFallback(fbIndex)} className="text-fg-muted hover:text-danger-500 p-1 transition-colors"><PiTrashLight size={16} /></button>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  <Button onClick={() => pushFallback('')} variant='outline' size='small'>
                    <PiPlusLight size={16} /> {t('Add fallback')}
                  </Button>
                </div>
              )}
            </FieldArray>
          </div>
          <div className="pt-4 border-t border-hairline-soft">
            <h5 className="text-sm font-medium text-fg-body flex items-center gap-2 mb-3">
              <PiStackSimpleLight size={16} className="text-fg-muted" /> {t('Subsections')}
              <span className="text-xs text-fg-muted font-normal">({item.subsections?.length || 0})</span>
            </h5>
            <FieldArray name={`Structure.outline.${index}.subsections`}>
              {({ remove: removeSub, push: pushSub }) => (
                <div className="space-y-4">
                  {item.subsections.map((sub, subIndex) => (
                    <div key={subIndex} ref={(el) => { sectionRefs.current[sub.number!] = el; }}>
                    <div className="rounded-md border border-hairline p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="text-xs font-mono text-fg-muted bg-surface-sunken border border-hairline-soft px-1.5 py-0.5 rounded">{sub.number}</span>
                          {editingSubsectionKey === `${index}-${subIndex}` ? (
                            <div className="flex items-center gap-2 flex-1">
                              <Input
                                id={`outline-${index}-sub-${subIndex}-title`}
                                name={`Structure.outline.${index}.subsections.${subIndex}.title`}
                                value={sub.title}
                                onChange={formik.handleChange}
                                type="text"
                                placeholder={t('Subsection title...')}
                                autoFocus
                              />
                              <button onClick={() => setEditingSubsectionKey(null)} className="text-success-500 hover:text-success-600 p-1"><PiCheckLight size={16} /></button>
                              <button onClick={() => setEditingSubsectionKey(null)} className="text-danger-500 hover:text-danger-600 p-1"><PiXLight size={16} /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-1 cursor-pointer group" onClick={() => setEditingSubsectionKey(`${index}-${subIndex}`)}>
                              <h4 className="text-sm text-fg-strong break-words min-w-0">{cleanOutlineTitle(sub.title) || t('Untitled subsection')}</h4>
                              <PiPencilSimpleLight className="text-fg-muted opacity-0 group-hover:opacity-100 transition-opacity" size={14} />
                            </div>
                          )}
                        </div>
                        <button onClick={() => removeSub(subIndex)} className="text-fg-muted hover:text-danger-500 transition-colors">
                          <PiTrashLight size={16} />
                        </button>
                      </div>
                      <div className="space-y-3">
                        <Textarea
                          id={`outline-${index}-sub-${subIndex}-content-prompt`}
                          name={`Structure.outline.${index}.subsections.${subIndex}.content_prompt`}
                          label={t('Content prompt')}
                          value={sub.content_prompt}
                          onChange={formik.handleChange}
                          placeholder={t('Describe subsection content...')}
                          rows={3}
                          h="min-h-[60px]"
                        />
                        <Textarea
                          id={`outline-${index}-sub-${subIndex}-example-language`}
                          name={`Structure.outline.${index}.subsections.${subIndex}.example_language`}
                          label={t('Example language')}
                          value={sub.example_language}
                          onChange={formik.handleChange}
                          placeholder={t('Example phrasing...')}
                          rows={3}
                          h="min-h-[60px]"
                        />
                      </div>
                    </div>
                    </div>
                  ))}
                  <Button onClick={() => pushSub({ level: 2, title: '', content_prompt: '', example_language: '', fallbacks: [], subsections: [] })} variant='outline' size='small'>
                    <PiPlusLight size={16} /> {t('Add subsection')}
                  </Button>
                </div>
              )}
            </FieldArray>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const StructureForm: React.FC<StructureFormProps> = ({ templateId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { checkSectionGate, checkAiFeatureGate, hasAiFeatureAccess } = useLicenseGate();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const userNavigatedAwayRef = useRef(false); // Track if user navigated away during creation
  const [newTemplateId, setNewTemplateId] = useState<string | undefined>(templateId);
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);
  const [editingSubsectionKey, setEditingSubsectionKey] = useState<string | null>(null);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set());
  const templateRun = useSelector((state: RootState) => state.templates.run);
  const templateElapsed = useAIRunElapsed(templateRun);
  /* The run's own words for "we have asked and are waiting" - no separate
     connecting flag to keep in step with it. */
  const isConnecting = templateRun.status === 'connecting';
  const [isCreating, setIsCreating] = useState(false)
  /* Editing an existing template is a plain PUT - no AI job, no PubSub run.
     It gets its own quiet flag so it can never light up the create-time
     AIProgressPanel; the affect is the Save button's own spinner. */
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOrgWide, setIsOrgWide] = useState(false);
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const pubsubCleanupRef = useRef<(() => void) | null>(null);

  // ─── Tours ─────────────────────────────────────────────────────────
  const { isRunning: isCreateTourRunning, handleJoyrideCallback: handleCreateTourCallback } = useFeatureTour({
    tourId: 'create-structure-form',
    steps: createStructureFormTourSteps,
    readyToShow: !newTemplateId && !isFetching && !isCreating && !isSaving && !isConnecting,
    autoStartDelay: 600,
  });

  const { isRunning: isDetailTourRunning, handleJoyrideCallback: handleDetailTourCallback } = useFeatureTour({
    tourId: 'structure-detail',
    steps: structureDetailTourSteps,
    readyToShow: !!newTemplateId && !isFetching && !isCreating && !isSaving && !isConnecting && !error,
    autoStartDelay: 600,
  });

  // Sync newTemplateId with templateId prop when it changes (e.g., after redirect)
  useEffect(() => {
    if (templateId !== newTemplateId) {
      setNewTemplateId(templateId);
      fetchedRef.current = false; // Reset fetch flag to allow re-fetching
    }
  }, [templateId, newTemplateId]);

  // Cleanup: reset template creation state on unmount
  useEffect(() => {
    return () => {
      if (pubsubCleanupRef.current) {
        pubsubCleanupRef.current();
      }
      dispatch(resetTemplateCreation());
    };
  }, [dispatch]);

  const draftTypeOptions = [
    { value: '', label: t('Select document type') },
    { value: 'any', label: t('All types') },
    { value: 'agreement', label: t('Agreement') },
    { value: 'brief', label: t('Brief') },
    { value: 'complaint', label: t('Complaint') },
    { value: 'contract', label: t('Contract') },
    { value: 'correspondence', label: t('Correspondence') },
    { value: 'court-filing', label: t('Court filing') },
    { value: 'discovery', label: t('Discovery') },
    { value: 'engagement-letter', label: t('Engagement letter') },
    { value: 'legal-opinion', label: t('Legal opinion') },
    { value: 'memo', label: t('Memo') },
    { value: 'motion', label: t('Motion') },
    { value: 'nda', label: t('NDA') },
    { value: 'pleading', label: t('Pleading') },
    { value: 'policy', label: t('Policy') },
    { value: 'power-of-attorney', label: t('Power of attorney') },
    { value: 'proposal', label: t('Proposal') },
    { value: 'report', label: t('Report') },
    { value: 'settlement', label: t('Settlement') },
    { value: 'will-trust', label: t('Will / Trust') },
    { value: 'other', label: t('Other') },
  ];

  // ── BaseForm sections & schema for creation mode ──
  const createFormSections: BaseFormSectionConfig[] = useMemo(() => [
    {
      dataTourId: 'structure-form-name',
      title: t('Template details'),
      description: t('Name the template and say what kind of document it produces.'),
      gridCols: 2,
      fields: [
        {
          name: 'TemplateName',
          type: 'input',
          label: t('Template name'),
          placeholder: t('Enter template name'),
          required: true,
          gridSize: 'half',
        } as BaseFormFieldConfig,
        {
          name: 'DraftType',
          type: 'dropdown',
          label: t('Document type'),
          placeholder: t('Select document type'),
          required: true,
          options: draftTypeOptions,
          customProps: { fullWidth: true },
          gridSize: 'half',
        } as BaseFormFieldConfig,
      ],
    },
    {
      dataTourId: 'structure-form-sharing',
      title: t('Sharing'),
      description: t('Control who can access this template'),
      fields: [
        {
          name: 'is_org_wide',
          type: 'custom',
          label: '',
          gridSize: 'full',
          customComponent: OrgWideToggle,
          customProps: { title: t('Organization-wide template') },
        } as BaseFormFieldConfig,
        ...(!isOrgWide ? [{
          name: 'collaborators',
          type: 'collaboratorSelector',
          label: t('Access'),
          gridSize: 'full',
          customProps: {
            ownerLabel: t('You are the template owner'),
            showOwnerSection: true,
          },
        } as BaseFormFieldConfig] : []),
      ],
    },
    {
      dataTourId: 'structure-form-upload',
      title: t('Upload document'),
      description: t('Upload a document to extract the template structure from.'),
      fields: [
        {
          name: 'document',
          type: 'fileUploader',
          label: t('Document'),
          required: true,
          gridSize: 'full',
          maxFiles: 1,
          allowedExtensions: ['docx', 'pdf'],
        } as BaseFormFieldConfig,
      ],
    },
  ], [t, draftTypeOptions, isOrgWide]);

  const createFormSchema = useMemo(() => {
    return new FormSchema('createStructureTemplate', [
      new FormField('TemplateName', 'text', true, t('Template name')),
      new FormField('DraftType', 'text', true, t('Document type')),
      new FormField('is_org_wide', 'text', false, t('Organization-wide')),
      new FormField('collaborators', 'text', false, t('Access')),
      new FormField('document', 'text', true, t('At least one document'), {
        customValidator: (value: any) => {
          if (!value || (Array.isArray(value) && value.length === 0)) {
            return { isValid: false, error: t('Upload a document to extract the template from') };
          }
          return { isValid: true };
        },
      }),
    ]);
  }, [t]);

  // Step ID mapping from AI Engine step names to frontend step IDs
  const STEP_NAME_TO_ID: Record<string, string> = {
    'Uploading document': 'upload',
    'Validating format': 'validate',
    'Extracting content': 'extract',
    'Analyzing structure': 'analyze',
    'Generating template': 'generate',
  };

  const connectToPubSub = useCallback(async (
    correlationId: string,
    templateUuid: string,
    onComplete?: () => void,
  ) => {
    const hub = 'ai_processing';
    const group = `template_${correlationId}`;
    const socketService = getMultiHubSocketService();

    try {
      const isConnected = await socketService.connectToHub(hub);
      if (!isConnected) {
        console.warn('[StructureForm] Failed to connect to PubSub hub');
        return;
      }

      // Join the template processing group
      socketService.emit(hub, 'join', { group });

      // Listen for all events
      const handleEvent = (eventData: any) => {
        // Only handle events for our correlation ID
        if (eventData?.correlationId !== correlationId && eventData?.correlation_id !== correlationId) {
          return;
        }

        const subType = eventData?.data?.sub_type || eventData?.sub_type || '';

        /*
          THE ENVELOPE GOES STRAIGHT IN. This used to unpack `sub_type` and
          `step_name` here, map the name through a local `STEP_NAME_TO_ID`
          table and dispatch a hand-shaped step patch - a second normaliser
          living in a component, which is exactly what `aiRun` exists to be.
          It reads `step_started` / `step_progress` / `step_completed` /
          `step_failed`, the percent, the totals and the narration itself.
        */
        dispatch({ type: TEMPLATE_CREATE_EVENT, payload: { raw: eventData } });

        if (subType === 'completed') {
          // Full pipeline complete - clean up PubSub, reset state
          if (pubsubCleanupRef.current) {
            pubsubCleanupRef.current();
            pubsubCleanupRef.current = null;
          }

          dispatch(resetTemplateCreation());
          setIsCreating(false);
          setIsSaving(false);

          if (onComplete) {
            // Custom completion handler (e.g., re-analysis refetch)
            onComplete();
          } else {
            // Default: show success + navigate (creation flow)
            dispatch(showFeedbackModal({
              showModal: true,
              message: t('Template created successfully!'),
              modalType: 'success',
              duration: 3000,
            }));
            history.push(`/templates/${templateUuid}?type=structure`);
          }
        } else if (subType === 'failed') {
          // Never surface a raw payload (a serialized outline/fallbacks blob
          // could carry privileged text). Both candidates are guarded; if
          // neither yields prose, log the detail and show a clean message.
          const error =
            toErrorText(eventData?.data?.error) ||
            ensureDisplayString(eventData?.message) ||
            t('Processing failed');
          if (error === t('Processing failed')) {
            console.error('[StructureForm] Non-displayable failure payload:', eventData);
          }

          // Clean up PubSub
          if (pubsubCleanupRef.current) {
            pubsubCleanupRef.current();
            pubsubCleanupRef.current = null;
          }

          dispatch({ type: TEMPLATE_CREATE_FAILED, payload: { error } });
          dispatch(showFeedbackModal({
            showModal: true,
            message: error,
            modalType: 'error',
            duration: 4000,
          }));
          setIsCreating(false);
          setIsSaving(false);
        }
      };

      socketService.on(hub, '*', handleEvent);

      // Store cleanup function
      pubsubCleanupRef.current = () => {
        socketService.off(hub, '*', handleEvent);
        socketService.emit(hub, 'leave', { group });
      };

    } catch (err) {
      console.error('[StructureForm] PubSub connection error:', err);
    }
  }, [dispatch, history, t]);

  const handleCreateSubmit = useCallback(async (formData: Record<string, any>) => {
    if (!checkSectionGate('templates')) return;
    if (!checkAiFeatureGate('template_ai')) return;
    setIsSaving(true);
    setIsCreating(true);

    const orgWide = !!formData.is_org_wide;
    const sharedWithIds = orgWide ? [] : (formData.collaborators || []).map((c: any) => c.collaborator_uuid || c.user_id).filter(Boolean);
    const data = {
      TemplateName: formData.TemplateName || '',
      DraftType: formData.DraftType,
      OwnerType: orgWide ? 'firm-wide' : 'personal',
      SharedWith: sharedWithIds,
    };
    const files: { [key: string]: File } = {};
    const uploadedFiles = formData.document;
    if (Array.isArray(uploadedFiles) && uploadedFiles.length > 0) {
      files.document = uploadedFiles[0];
    } else if (uploadedFiles instanceof File) {
      files.document = uploadedFiles;
    }

    try {
      userNavigatedAwayRef.current = false;
      const response = await dispatch(createStructureTemplate(data, files) as any);

      if (response?.status === 201 && response?.data?.uuid) {
        const { uuid, correlationId } = response.data;

        // Connect to PubSub for live progress updates from AI Engine
        if (correlationId) {
          await connectToPubSub(correlationId, uuid);
        } else {
          // No PubSub available - mark as completed immediately
          dispatch({ type: TEMPLATE_CREATE_COMPLETED, payload: { ok: true } });
          setIsCreating(false);
          setIsSaving(false);
          history.push(`/templates/${uuid}?type=structure`);
        }
        return;
      }
      setIsCreating(false);
      setIsSaving(false);
    } catch (err) {
      if (!err) return;
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Failed to save template.'),
        showModal: true,
        duration: 3000,
      }));
      setIsCreating(false);
      setIsSaving(false);
    }
  }, [dispatch, history, t, connectToPubSub]);

  const createValidationSchema = Yup.object({
    DraftType: Yup.string().required(t('Document type is required')),
    OwnerType: Yup.string().optional(),
    TemplateName: Yup.string().required(t('Template name is required')),
    document: Yup.mixed().required(t('Document is required')),
  });

  const editValidationSchema = Yup.object({
    TemplateName: Yup.string().required(t('Name is required')),
    DraftType: Yup.string().required(t('Document type is required')),
    OwnerType: Yup.string().optional(),
    Description: Yup.string(),
    Structure: Yup.object({
      outline: Yup.array().min(1, t('At least one section is required')).of(
        Yup.object({
          title: Yup.string().required(t('Section title is required')),
          content_prompt: Yup.string(),
          example_language: Yup.string(),
          fallbacks: Yup.array().of(Yup.string()),
          subsections: Yup.array().of(
            Yup.object({
              title: Yup.string().required(t('Subsection title is required')),
              content_prompt: Yup.string(),
              example_language: Yup.string(),
              fallbacks: Yup.array().of(Yup.string()),
            })
          ),
        })
      ),
    }),
    Tone: Yup.string(),
    WritingStyle: Yup.string(),
  });

  const formik = useFormik<StructureFormValues>({
    initialValues: {
      TemplateName: '',
      DraftType: '',
      OwnerType: 'personal',
      SharedWith: [],
      Description: '',
      Structure: { outline: [] },
      Tone: '',
      WritingStyle: '',
      document: null,
      CreatedAt: '',
      UpdatedAt: '',
      Owner: { 
        Name: userInfo.FullName || '', 
        Email: userInfo.Email || '', 
        UserID: userInfo.UserUID || userInfo.uuid_encrypted || '', 
        Initials: userInfo.FullName ? userInfo.FullName.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() : 'U', 
        ProfilePicture: userInfo.ProfilePicture || ''
      }
    },
    validationSchema: newTemplateId ? editValidationSchema : createValidationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!checkSectionGate('templates')) return;
      if (!checkAiFeatureGate('template_ai')) return;
      if (newTemplateId) {
        setIsUpdating(true);
      } else {
        setIsSaving(true);
        setIsCreating(true);
      }
      // Convert collaborators to SharedWith format (array of user IDs)
      const sharedWithIds = isOrgWide ? [] : collaborators.map(c => c.collaborator_uuid || c.user_id).filter(Boolean);
      const data = {
        TemplateName: values.TemplateName,
        DraftType: values.DraftType,
        OwnerType: isOrgWide ? 'firm-wide' : (values.OwnerType || 'personal'),
        SharedWith: sharedWithIds,
        ...(newTemplateId ? {
          Description: values.Description,
          Structure: { outline: normalizeOutline(values.Structure?.outline) },
          Tone: values.Tone,
          WritingStyle: values.WritingStyle,
        } : {}),
      };
      const files: { [key: string]: File } = {};
      if (values.document) {
        files.document = values.document;
      }
      try {
        let response;
        if (newTemplateId) {
          response = await dispatch(updateStructureTemplate(newTemplateId, data, files));
        } else {
          userNavigatedAwayRef.current = false;
          
          response = await dispatch(createStructureTemplate(data, files) as any);
          
          if (response?.status === 201 && response?.data?.uuid) {
            const { uuid, correlationId } = response.data;
            
            // Connect to PubSub for live progress updates
            if (correlationId) {
              await connectToPubSub(correlationId, uuid);
            } else {
              dispatch({ type: TEMPLATE_CREATE_COMPLETED, payload: { ok: true } });
              setIsCreating(false);
              setIsSaving(false);
              history.push(`/templates/${uuid}?type=structure`);
            }
            return;
          }
        }
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: newTemplateId ? t('Template updated!') : t('Template created!'),
          showModal: true,
          duration: 3000
        }));
      } catch (err) {
        if(!err) return 
        dispatch(showFeedbackModal({
          modalType: 'error',
          message: t('Failed to save template.'),
          showModal: true,
          duration: 3000
        }));
      } finally {
        setIsCreating(false)
        setIsSaving(false)
        setIsUpdating(false)
      }
    },
    enableReinitialize: true,
  });

const mapAiToForm = (template: any) => {
  const aiStructure = template.Structure || {};
  let sharedWith = template.SharedWith || [];
  if (typeof sharedWith === 'string') {
    try {
      sharedWith = JSON.parse(sharedWith);
    } catch (e) {}
  }
  
  // Build owner from template or fallback to current user
  const owner = template.Owner && template.Owner.UserID 
    ? template.Owner 
    : { 
        UserID: userInfo.UserUID || userInfo.uuid_encrypted || '', 
        Name: userInfo.FullName || '', 
        Email: userInfo.Email || '', 
        ProfilePicture: userInfo.ProfilePicture || null, 
        Initials: userInfo.FullName ? userInfo.FullName.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase() : 'U' 
      };
  
  const ownerType = template.OwnerType || 'personal';
  setIsOrgWide(ownerType === 'firm-wide');
  formik.setValues({
    TemplateName: template.TemplateName || aiStructure.suggested_name || '',
    DraftType: template.DraftType || '',
    OwnerType: ownerType,
    SharedWith: sharedWith,
    Description: template.Description || aiStructure.description || '',
    Structure: { outline: normalizeOutline(aiStructure.outline) },
    Tone: template.Tone || aiStructure.tone || '',
    WritingStyle: template.WritingStyle || aiStructure.writing_style || '',
    document: null,
    CreatedAt: template.CreatedAt,
    UpdatedAt: template.UpdatedAt,
    Owner: owner,
  });
};

 /**
  * Clean an extracted outline before it enters the form or goes back to the
  * engine. The AI extraction sometimes returns fallback phrases wrapped in a
  * literal pair of quote characters - e.g. the string is `"PRIVILEGED &
  * CONFIDENTIAL"` including the quotes - which then renders as odd, "already
  * quoted" text and round-trips into every later payload. We strip one matched
  * surrounding quote pair (straight or curly) and trim, and drop empties. This
  * only touches obviously-wrapped values; ordinary phrasing is left untouched.
  */
 const stripWrappingQuotes = (value: unknown): string => {
   if (typeof value !== 'string') return '';
   let s = value.trim();
   const pairs: Array<[string, string]> = [['"', '"'], ['“', '”'], ["'", "'"]];
   for (const [open, close] of pairs) {
     if (s.length >= 2 && s.startsWith(open) && s.endsWith(close)) {
       s = s.slice(1, -1).trim();
       break;
     }
   }
   return s;
 };

 const normalizeFallbacks = (fallbacks: unknown): string[] =>
   Array.isArray(fallbacks)
     ? fallbacks.map(stripWrappingQuotes).filter((f) => f.length > 0)
     : [];

 const normalizeOutline = (outline: unknown): OutlineItem[] => {
   if (!Array.isArray(outline)) return [];
   return outline.map((item: any) => ({
     ...item,
     fallbacks: normalizeFallbacks(item?.fallbacks),
     subsections: normalizeOutline(item?.subsections),
   }));
 };

 const assignNumbers = (outline: OutlineItem[] | undefined) => {
    if (!outline || !Array.isArray(outline)) {
        return [];  
    }
    let sectionNum = 1;
    return outline.map((item) => {
        const numberedItem = { ...item, number: `${sectionNum}` };
        let subNum = 1;
        numberedItem.subsections = (numberedItem.subsections || []).map((sub) => ({ ...sub, number: `${sectionNum}.${subNum++}` }));  
        sectionNum++;
        return numberedItem;
    });
    };

  const numberedOutline = assignNumbers(formik.values.Structure?.outline);

  useEffect(() => {
    setOpenSections(new Set(numberedOutline.map((_, index) => index)));
  }, [numberedOutline.length]);

  const toggleSection = (index: number) => {
    const newOpenSections = new Set(openSections);
    if (newOpenSections.has(index)) {
      newOpenSections.delete(index);
    } else {
      newOpenSections.add(index);
    }
    setOpenSections(newOpenSections);
  };

  const formatOutline = (outline: (OutlineItem & { number: string })[]): string => {
    let result = '';
    const formatItem = (item: OutlineItem & { number: string }, indent: string = '') => {
      result += `${indent}${item.number} ${item.title}\n`;
      if (item.content_prompt) {
        result += `${indent}Content Prompt: ${item.content_prompt}\n`;
      }
      if (item.example_language) {
        result += `${indent}Example Language: ${item.example_language}\n`;
      }
      if (item.fallbacks?.length > 0) {
        result += `${indent}Fallbacks:\n`;
        item.fallbacks.forEach((fallback, fbIndex) => {
          result += `${indent}  ${fbIndex + 1}: ${fallback}\n`;
        });
      }
      if (item.subsections?.length > 0) {
        item.subsections.forEach((sub: any) => formatItem(sub, indent + '  '));
      }
      result += '\n';
    };
    outline.forEach(item => formatItem(item));
    return result.trim();
  };

  const copyOutline = () => {
    navigator.clipboard.writeText(formatOutline(numberedOutline));
    dispatch(showFeedbackModal({ modalType: 'success', message: t('Outline copied to clipboard!'), showModal: true, duration: 2000 }));
  };

    const handleDelete = async () => {
    dispatch(showModal({
        showModal: true,
        type: "confirmation",
        isDest: true,
        icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
        title: t("Delete template"),
        message: t("Are you sure you want to delete this template?"),
        subText: t("This action cannot be undone."),
        variant: 'danger',
        confirmLabel: t("Yes, delete"),
        onConfirm: async () => {
        dispatch(hideModal());
        try {
            const resp = await dispatch(deleteStructureTemplate(newTemplateId!) as any);
            // deleteStructureTemplate returns response.data on 200, throws on error
            if (resp) {
              history.push('/templates?type=structure');
            }
        } catch (err: any) {
            // Action already shows error feedback via handleApiError
            console.error('[StructureForm] Delete failed:', err);
        }
        },
        onCancel: () => dispatch(hideModal()),
        duration: 3000,
        inputData: t("Yes, delete"),
    }));
    };

  const handleAccessControl = () => {
    // Document templates use a single bulk-share endpoint (POST /templates/share),
    // not per-row CRUD. Each adapter callback computes the new SharedWith list and
    // sends the full payload, so the unified sidepanel can still drive add/remove/access.
    const buildPayload = (sharedWith: any[]) => ({
      TemplateID: newTemplateId,
      SharedWith: sharedWith,
    });

    const currentSharedWith = (): any[] => formik.values.SharedWith || [];

    const findUser = (userUuid: string): any | null => {
      // The CollaboratorSelector pushes a Collaborator with user_id; we mirror it
      // back into the legacy SharedWith shape used by /templates/share.
      const match = (formik.values.SharedWith || []).find(
        (s: any) => String(s.UserID) === String(userUuid)
      );
      return match || { UserID: userUuid, Name: '', Email: '', Initials: '', ProfilePicture: null };
    };

    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage access'),
      resourceUuid: newTemplateId || '',
      resourceType: 'template',
      collaborators: (formik.values.SharedWith || []).map((s: any) => ({
        user_id: s.UserID,
        collaborator_uuid: s.UserID,
        name: s.Name || '',
        email: s.Email || '',
        initials: s.Initials || '',
        profile_picture: s.ProfilePicture || null,
        access: s.Access || 'edit',
        role: 'collaborator',
      })),
      currentUserUuid: userInfo?.UserUID,
      canAdmin: true,
      addCollaborator: async (_uuid: string, userUuid: string, access: string) => {
        const base = findUser(userUuid);
        const next = [...currentSharedWith(), { ...base, UserID: userUuid, Access: access }];
        formik.setFieldValue('SharedWith', next);
        await dispatch(shareTemplate(buildPayload(next)) as any);
      },
      removeCollaborator: async (_uuid: string, collabUuid: string) => {
        const next = currentSharedWith().filter((s: any) => String(s.UserID) !== String(collabUuid));
        formik.setFieldValue('SharedWith', next);
        await dispatch(shareTemplate(buildPayload(next)) as any);
      },
      updateAccess: async (_uuid: string, collabUuid: string, access: string) => {
        const next = currentSharedWith().map((s: any) =>
          String(s.UserID) === String(collabUuid) ? { ...s, Access: access } : s
        );
        formik.setFieldValue('SharedWith', next);
        await dispatch(shareTemplate(buildPayload(next)) as any);
      },
      onUpdate: () => {},
    } as any));
  };

  const handleReanalyze = async () => {
    if (!newTemplateId) return;
    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      title: t('Re-analyze template'),
      message: t('The document will be re-processed by AI. Current outline and metadata will be replaced with freshly extracted data.'),
      icon: <PiArrowsClockwiseLight className="w-6 h-6 text-info-600" />,
      variant: 'info',
      confirmLabel: t('Re-analyze'),
      onConfirm: async () => {
        dispatch(hideModal());
        setIsCreating(true);
        setIsSaving(true);
        try {
          const result = await dispatch(reanalyzeStructureTemplate(newTemplateId) as any);
          if (result?.processing?.correlationId) {
            await connectToPubSub(result.processing.correlationId, newTemplateId, async () => {
              // Show skeleton loader immediately so old data is never visible
              setIsFetching(true);
              try {
                const refreshed = await dispatch(getStructureTemplate(newTemplateId) as any);
                if (refreshed) {
                  mapAiToForm(refreshed);
                }
                dispatch(showFeedbackModal({
                  showModal: true,
                  message: t('Template re-analyzed successfully!'),
                  modalType: 'success',
                  duration: 3000,
                }));
              } catch (fetchErr) {
                console.error('[StructureForm] Failed to refetch after re-analysis:', fetchErr);
                fetchedRef.current = false;
                dispatch(showFeedbackModal({
                  showModal: true,
                  message: t('Re-analysis complete. Please refresh to see updates.'),
                  modalType: 'warning',
                  duration: 4000,
                }));
              } finally {
                setIsFetching(false);
              }
            });
          } else {
            setIsCreating(false);
            setIsSaving(false);
          }
        } catch (err) {
          setIsCreating(false);
          setIsSaving(false);
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Failed to start re-analysis.'),
            showModal: true,
            duration: 3000,
          }));
        }
      },
      onCancel: () => dispatch(hideModal()),
    }));
  };
  
  // const [showAccessControlModal, setShowAccessControlModal] = useState(false); // Replaced with side panel

  const handleEditField = (field: 'Description' | 'Tone' | 'WritingStyle', title: string, rows: number = 3, placeholder: string = '') => {
    dispatch(showModal({
      type: `edit${field}`,
      message: t(title),
      subMsg: t('Write and save the changes you need.'),
      icon: <PiPencilSimpleLight className='w-6 h-6 text-fg-muted' />,
      inputData: formik.values[field],
      onConfirm: (newValue: string) => {
        formik.setFieldValue(field, newValue);
        dispatch(hideModal());
      },
      onCancel: () => dispatch(hideModal()),
      showModal: true,
      rows,
      placeholder,
    }));
  };

  useEffect(() => {
    if (newTemplateId && !fetchedRef.current) {
      fetchedRef.current = true;
      setIsFetching(true);
      dispatch(getStructureTemplate(newTemplateId))
        .then((template) => {
          if (template) {
            // Check if template is still being processed by AI Engine
            const templateStatus = template.status || template.Status;
            if (templateStatus === 'pending' || templateStatus === 'processing') {
              // Template is still processing - poll until complete
              setIsCreating(true);
              const pollInterval = setInterval(async () => {
                try {
                  const updated = await dispatch(checkTemplateStatus(newTemplateId));
                  const updatedStatus = updated?.status || updated?.Status;
                  if (updatedStatus === 'completed' || updatedStatus === 'failed') {
                    clearInterval(pollInterval);
                    if (updatedStatus === 'completed') {
                      const refreshed = await dispatch(getStructureTemplate(newTemplateId));
                      if (refreshed) {
                        mapAiToForm(refreshed);
                      }
                    }
                    setIsCreating(false);
                    setIsFetching(false);
                  }
                } catch (err) {
                  console.error('Poll status error:', err);
                  clearInterval(pollInterval);
                  setIsCreating(false);
                  setIsFetching(false);
                }
              }, 3000);
            } else {
              mapAiToForm(template);
              setIsFetching(false);
            }
          } else {
            setError(t('Template not found'));
            setIsFetching(false);
          }
        })
        .catch((err) => {
          console.error('Fetch error:', err);
          setError(t('Error loading template'));
          setIsFetching(false);
        });
    }
  }, [newTemplateId, dispatch, t]);

  const templateBreadcrumbs = [
    { name: t('Templates'), href: '/templates?type=structure', current: false },
    { name: formik.values.TemplateName || t('Create new template'), href: newTemplateId ? `/templates/${newTemplateId}?type=structure` : '/templates?type=structure', current: true },
  ];

  const templateDropdownActions = [
    {
      label: t('Save'),
      onClick: () => formik.handleSubmit(),
      icon: <PiCheckLight className="h-4 w-4" />,
      disabled: isSaving || isUpdating || !hasAiFeatureAccess('template_ai'),
    },
    {
      label: t('Copy outline'),
      onClick: copyOutline,
      icon: <PiCopyLight className="h-4 w-4" />,
    },
    {
      label: t('Markdown source'),
      onClick: () => {
        if (!newTemplateId) return;
        dispatch(showSidePanel({
          type: 'templateMarkdownEdit',
          showSidePanel: true,
          width: '55%',
          templateId: newTemplateId,
          templateName: formik.values.TemplateName,
        }));
      },
      icon: <PiFileTextLight className="h-4 w-4" />,
      disabled: !newTemplateId,
    },
    {
      label: t('Access control'),
      onClick: handleAccessControl,
      icon: <PiUsersThreeLight className="h-4 w-4" />,
    },
    {
      label: t('Re-analyze'),
      onClick: handleReanalyze,
      icon: <PiArrowsClockwiseLight className="h-4 w-4" />,
      disabled: !newTemplateId || isCreating,
    },
    {
      label: t('Delete'),
      onClick: handleDelete,
      icon: <PiTrashLight className="h-4 w-4 text-danger-900" />,
      variant: 'destructive',
      disabled: !newTemplateId, 
    },
  ];

  /**
   * Header faces, in the shape `accessModel` reads.
   *
   * `Name` / `Email` / `ProfilePicture` / `Initials` / `Access` are all keys
   * `accessModel.normalise` knows, so the rows go through unmapped and each
   * person's real picture reaches `ProfileImage`. The hand-rolled badge stack
   * this replaces derived initials inline and never showed photos.
   */
  const headerCollaborators = [
    ...((formik.values.Owner?.UserID || formik.values.Owner?.Name || formik.values.Owner?.Email)
      ? [{ ...formik.values.Owner, role: 'creator', access: 'admin' }]
      : []),
    ...(Array.isArray(formik.values.SharedWith) ? formik.values.SharedWith : [])
      .filter((s: any) => s.UserID || s.Initials || s.Email || s.Name)
      .map((s: any) => ({ ...s, access: s.Access || 'edit' })),
  ];

  const additionalHeaderElements = (
    <div className="flex items-center gap-3">
      {/* Collaborators with access - owner first, then shared users */}
      <CollaboratorStack
        collaborators={headerCollaborators}
        max={4}
        size="md"
        onClick={newTemplateId ? handleAccessControl : undefined}
        testId="structure-template-collaborator-stack"
      />

      {/* Save button */}
      <Button size='small' onClick={async () => {
        const errors = await formik.validateForm();
        if (Object.keys(errors).length > 0) {
          // Show validation error message
          const flatErrors: string[] = [];
          const extractErrors = (obj: any, prefix = ''): void => {
            for (const key in obj) {
              if (typeof obj[key] === 'string') {
                flatErrors.push(obj[key]);
              } else if (typeof obj[key] === 'object') {
                extractErrors(obj[key], `${prefix}${key}.`);
              }
            }
          };
          extractErrors(errors);
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: flatErrors[0] || t('Please fix validation errors before saving.'),
            showModal: true,
            duration: 4000
          }));
          return;
        }
        formik.handleSubmit();
      }} variant="primary" isLoading={isUpdating}>
        {t('Save')}
      </Button>

      {/* Three-dot menu - simple styling */}
      <Menu as="div" className="relative inline-block text-start z-50">
        <Menu.Button as={Button} size="small" variant="outline" className="p-1">
          <PiDotsThreeVerticalLight className="h-4 w-4" />
        </Menu.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <Menu.Items className="absolute end-0 mt-2 w-56 origin-top-right divide-y divide-hairline-soft rounded-md glass shadow-lg focus:outline-none">
            <div className="px-1 py-1">
              {templateDropdownActions.map((action, index) => (
                <Menu.Item key={index}>
                  {({ active }) => (
                    <button
                      className={clsx(
                        'group flex w-full items-center rounded-md px-2 py-2 text-sm gap-2',
                        active ? 'bg-surface-soft text-fg-strong' : 'text-fg-body',
                        action.variant === 'destructive' && 'text-danger-600 dark:text-danger-400'
                      )}
                      onClick={action.onClick}
                      disabled={action.disabled}
                    >
                      {action.icon}
                      {action.label}
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  );


  if (isFetching) {
    return (
      /* The SAME columns the loaded page has - glass panels at the same `p-3`
         dock gutter, not flat `bg-surface` slabs behind hard borders. A
         skeleton that does not match its loaded counterpart moves the page
         when the content lands, which is the one thing a skeleton exists to
         prevent. */
      <div className="flex h-full overflow-hidden bg-bg">
        <aside className="w-72 min-w-[200px] max-w-xs flex-shrink-0 p-3">
          <div className="glass rounded-lg shadow-sm h-full p-2">
            <Skeleton variant="list" rows={6} inset="none" />
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-3 overflow-y-auto custom-scrollbar-main">
          <Skeleton variant="form" rows={3} inset="none" />
        </main>

        <aside className="w-64 min-w-[200px] flex-shrink-0 p-3">
          <div className="glass rounded-lg shadow-sm h-full p-2">
            <Skeleton variant="card" rows={3} inset="none" />
          </div>
        </aside>
      </div>
    );
  }

  if (isCreating || isSaving || isConnecting) {
    const handleCancelTemplateCreation = () => {
      // Mark that user navigated away - PubSub completion should not redirect
      userNavigatedAwayRef.current = true;
      
      // Reset template creation state
      dispatch(resetTemplateCreation());
      setIsCreating(false);
      setIsSaving(false);
      
      // Navigate back to templates list
      history.push('/templates?type=structure');
    };

    /*
      THE SHARED PANEL, in the showcase layout matter creation uses - this is
      the screen for a minute or two, so it is laid out like a page.

      What it replaces: `SSECreationProgress` reading a bespoke step list that
      was seeded with five guesses. Every stage here has been named by the
      engine, and `percent` stays null until the engine sends one, which the
      panel draws as a moving sliver rather than a number nobody measured.
    */
    return (
      <div className="flex h-full min-h-[24rem] items-center justify-center bg-bg">
        <AIProgressPanel
          run={{ ...templateRun, elapsedSeconds: templateElapsed }}
          layout="centered"
          density="showcase"
          title={t('Creating template…')}
          completedTitle={t('Template created')}
          failedTitle={t('Template creation failed')}
          cancelledTitle={t('Template creation cancelled')}
          hint={
            <p className="text-center text-sm leading-relaxed text-fg-muted">
              {t('Your document is being read and turned into a reusable structure. You can safely leave this open.')}
            </p>
          }
          onCancel={handleCancelTemplateCreation}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center space-y-4 bg-bg">
        <Badge color="red">{error}</Badge>
        <Button variant="primary" size="small" onClick={() => history.push('/templates?type=structure')}>
          {t('Back to templates')}
        </Button>
      </div>
    );
  }

if (!newTemplateId) {
    return (
      <>
      <BaseForm
        title={t('Create new template')}
        hideHeader
        leadIn={
          <TemplateTypeSwitcher
            current="structure"
            variant="header"
            title={t('Create new template')}
            description={t('Fill out this form to add your template.')}
          />
        }
        sections={createFormSections}
        schema={createFormSchema}
        initialValues={{
          TemplateName: '',
          DraftType: '',
          is_org_wide: false,
          collaborators: [],
          document: [],
        }}
        onSubmit={handleCreateSubmit}
        onCancel={() => history.goBack()}
        onValuesChange={(values) => {
          if (values.is_org_wide !== isOrgWide) {
            setIsOrgWide(!!values.is_org_wide);
          }
        }}
        submitLabel={t('Create template')}
        cancelLabel={t('Cancel')}
        isSubmitting={isSaving}
        hideLoadingOverlay={true}
        maxWidth="max-w-3xl"
        submitDisabled={!hasAiFeatureAccess('template_ai')}
        submitTooltip={t('Upgrade your plan to use AI Templates.')}
      />
      <FeatureTour
        steps={createStructureFormTourSteps}
        run={isCreateTourRunning}
        onCallback={handleCreateTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
      </>
    );
  }
  


  return (
    <DetailFrame
      breadcrumbs={templateBreadcrumbs}
      dropdownActions={[]}
      subscriptionActive={true}
      additionalHeaderElements={additionalHeaderElements}
      showBackButton={true}
      className="!flex-row flex-1 overflow-hidden bg-bg !px-0"
    >
    <FormikProvider value={formik}>
            {/*
              A PANE, not a slab. Both columns were `bg-surface` behind a hard
              `border-e`/`border-s` - the flat treatment the design system
              reserves for structural chrome like the sidebar. These hold
              content, so they are glass panels floating on the canvas, which
              is what the reviews and matter surfaces do with the same kind of
              column (`shared/surface/Panel` → `GlassCard padding="none"
              density="compact"`). Same `p-3` dock gutter, same
              `border-hairline-soft` header rule, same `px-3 py-2` header.
            */}
            <aside data-tour-id="structure-detail-sidebar" className="w-72 min-w-[200px] max-w-xs flex-shrink-0 relative flex flex-col p-3">
            <div className="glass rounded-lg shadow-sm flex flex-col flex-1 min-h-0 overflow-hidden">
            <div className="flex gap-2 items-center px-3 py-2 border-b border-hairline-soft">
                {editingField === 'templateName' ? (
                <div className="flex items-center flex-1 min-w-0">
                    <Input
                    name="TemplateName"
                    value={formik.values.TemplateName}
                    onChange={formik.handleChange}
                    onBlur={() => setEditingField(null)}
                    className="text-sm w-full max-w-full overflow-hidden"
                    type="text"
                    />
                    <PiPencilSimpleLight className="inline text-fg-muted flex-shrink-0 ms-2" size={16} />
                </div>
                ) : (
                <div onClick={() => setEditingField('templateName')} className="flex items-center justify-between flex-1 min-w-0 cursor-pointer group">
                    <h2 className="text-sm text-fg-strong truncate">{formik.values.TemplateName || t('Untitled template')}</h2>
                    <PiPencilSimpleLight className="inline text-fg-muted flex-shrink-0 ms-2 opacity-0 group-hover:opacity-100 transition-opacity" size={18} />
                </div>
                )}
            </div>
            <ul className="space-y-2 text-fg-body p-4 overflow-y-auto custom-scrollbar-main flex-1 min-h-0">
                <AnimatePresence>
                {numberedOutline.map((item, index) => (
                    <motion.li
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="text-sm"
                    >
                    <div className="flex items-start justify-between gap-1 py-1 px-2 rounded hover:bg-surface-soft transition-colors">
                        <span
                          className="flex min-w-0 cursor-pointer hover:text-fg-strong transition-colors"
                          title={cleanOutlineTitle(item.title) || undefined}
                          onClick={() => sectionRefs.current[item.number]?.scrollIntoView({ behavior: 'smooth' })}
                        >
                        <span className="text-fg-muted me-2 flex-shrink-0 tabular-nums">{item.number}</span>
                        <span className="min-w-0 break-words line-clamp-2">{cleanOutlineTitle(item.title) || t('Untitled section')}</span>
                        </span>
                        {item.subsections.length !== 0 && (
                        <button onClick={() => toggleSection(index)} className="text-fg-muted hover:text-fg-body p-1">
                            {openSections.has(index) ? <PiCaretDownLight size={14} /> : <PiCaretRightLight size={14} className="rtl:-scale-x-100" />}
                        </button>
                        )}
                    </div>
                    <AnimatePresence>
                        {openSections.has(index) && item.subsections.map((sub, subIndex) => (
                        <motion.div
                            key={subIndex}
                            variants={subsectionVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="ms-6 py-1 text-xs text-fg-muted cursor-pointer hover:text-fg-body transition-colors flex min-w-0"
                            title={cleanOutlineTitle(sub.title) || undefined}
                            onClick={() => sectionRefs.current[sub.number!]?.scrollIntoView({ behavior: 'smooth' })}
                        >
                            <span className="text-fg-muted me-2 flex-shrink-0 tabular-nums">{sub.number}</span>
                            <span className="min-w-0 break-words line-clamp-2">{cleanOutlineTitle(sub.title) || t('Untitled subsection')}</span>
                        </motion.div>
                        ))}
                    </AnimatePresence>
                    </motion.li>
                ))}
                </AnimatePresence>
            </ul>
            </div>
            </aside>

          <main data-tour-id="structure-detail-editor" className="flex-1 min-w-0 p-3 overflow-y-auto bg-bg custom-scrollbar-main">
            <div className="space-y-3">
              <AnimatePresence>
                {numberedOutline.map((item, index) => (
                  <DraggableSection
                    key={index}
                    item={item}
                    index={index}
                    formik={formik}
                    setEditingSectionIndex={setEditingSectionIndex}
                    sectionRefs={sectionRefs}
                    editingSectionIndex={editingSectionIndex}
                    editingSubsectionKey={editingSubsectionKey}
                    setEditingSubsectionKey={setEditingSubsectionKey}
                  />
                ))}
              </AnimatePresence>
              <button onClick={() => formik.setFieldValue('Structure.outline', [...formik.values.Structure.outline, { level: 1, title: '', content_prompt: '', example_language: '', fallbacks: [], subsections: [] }])} className="text-sm text-fg-muted hover:text-champagne transition-colors flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-hairline hover:border-champagne w-full justify-center">
                <PiPlusLight size={16} /> {t('Add section')}
              </button>
            </div>
          </main>

          <aside data-tour-id="structure-detail-metadata" className="w-64 min-w-[200px] flex-shrink-0 p-3 flex flex-col overflow-hidden">
            <div className="glass rounded-lg shadow-sm flex flex-col flex-1 gap-2 min-h-0 p-2">
              {/* Read-only metadata that opens an editor on click - display
                  values, not form fields, so they use the Field label/value
                  standard (§12.2b) rather than a <label> labelling nothing. */}
              <Well
                className="flex-1 min-h-0 cursor-pointer hover:bg-surface-sunken transition-colors overflow-y-auto custom-scrollbar-main"
                onClick={() => handleEditField('Description', t('Edit description'), 10, t('Enter description here...'))}
              >
                <Field
                  icon={<PiTextAlignLeftLight />}
                  label={t('Description')}
                  emptyText={t('Click to add description...')}
                >
                  {formik.values.Description ? (
                    <p className="text-sm font-normal text-fg-body leading-relaxed whitespace-pre-wrap break-words">{formik.values.Description}</p>
                  ) : undefined}
                </Field>
              </Well>
              <Well
                className="flex-1 min-h-0 cursor-pointer hover:bg-surface-sunken transition-colors overflow-y-auto custom-scrollbar-main"
                onClick={() => handleEditField('Tone', t('Edit tone'), 3, t('Enter tone here...'))}
              >
                <Field
                  icon={<PiUserSound />}
                  label={t('Tone')}
                  emptyText={t('Click to set tone...')}
                >
                  {formik.values.Tone ? (
                    <p className="text-sm font-normal text-fg-body leading-relaxed whitespace-pre-wrap break-words">{formik.values.Tone}</p>
                  ) : undefined}
                </Field>
              </Well>
              <Well
                className="flex-1 min-h-0 cursor-pointer hover:bg-surface-sunken transition-colors overflow-y-auto custom-scrollbar-main"
                onClick={() => handleEditField('WritingStyle', t('Edit writing style'), 3, t('Enter writing style here...'))}
              >
                <Field
                  icon={<PiArticleLight />}
                  label={t('Writing style')}
                  emptyText={t('Click to set style...')}
                >
                  {formik.values.WritingStyle ? (
                    <p className="text-sm font-normal text-fg-body leading-relaxed whitespace-pre-wrap break-words">{formik.values.WritingStyle}</p>
                  ) : undefined}
                </Field>
              </Well>
            </div>
          </aside>
    </FormikProvider>
    
    {/* Access Control Modal - replaced with side panel via handleAccessControl */}
    <FeatureTour
      steps={structureDetailTourSteps}
      run={isDetailTourRunning}
      onCallback={handleDetailTourCallback}
      continuous
      showProgress
      showSkipButton
      disableOverlay
    />
    </DetailFrame>
  );
};

export default StructureForm;
